import { apiFiles } from "@/api/file.api.ts";
import { getEmojiColorPalette, getRandomDefaultEmoji } from "@/components/emoji/emoji-colors.ts";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { GradientContainer } from "@/components/gradient-container/GradientContainer.tsx";
import {
  parseUrlHelper,
  Avatar,
  Button,
  DotSeparator,
  FullScreenPage,
  Skeleton,
  Stack,
  Text,
} from "@skailer-com/uikit";
import { toNullableSalaryNumber } from "@/helpers/utils.helper.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import i18n from "@/i18n.ts";
import {
  JobEditDataContext,
  JobEditDataContextType,
  JobEditFormContext,
  JobEditFormData,
  JobEditSystemFormData,
} from "@/pages/recruit/jobs/edit/components/job-edit.type.ts";
import { JobEditCareer } from "@/pages/recruit/jobs/edit/components/JobEditCareer.tsx";
import { JobEditDetails } from "@/pages/recruit/jobs/edit/components/JobEditDetails.tsx";
import { JobEditFinal } from "@/pages/recruit/jobs/edit/components/JobEditFinal.tsx";
import { JobEditSidebar } from "@/pages/recruit/jobs/edit/components/JobEditSidebar.tsx";
import { toJobApplyFormSave } from "@/pages/recruit/jobs/edit/job-apply-form.utils.ts";
import {
  getJobFormSections,
  isJobFieldValueEmpty,
  isJobMandatoryStatus,
  jobFieldNames,
} from "@/pages/recruit/jobs/edit/job-edit-fields.ts";
import { JobEditStep } from "@/pages/recruit/jobs/edit/job-edit.types.ts";
import { usePositionRequestDetails } from "@/queries/recruit/use-hiring-plan.query.ts";
import { useJobDetails, useJobSave } from "@/queries/recruit/use-jobs.query.ts";
import { usePipelines } from "@/queries/recruit/use-pipelines.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiCFEntityTypes, ApiFieldTypes, ApiFieldValueTypes } from "@/types/custom-field.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiJob, ApiJobSavePayload, JobStatus, JobPriority } from "@/types/recruit/job.types.ts";
import { ApiHiringPlanRequest } from "@/types/recruit/hiring-plan.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { Path, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

export const JobEditPage = () => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const positionRequestId = parseUrlHelper.getNumber(searchParams, "position_request_id");
  const jobId = id ? Number(id) : 0;
  const { data: job, isLoading: isJobLoading } = useJobDetails(jobId);
  const { data: positionRequest, isLoading: isPositionRequestLoading } = usePositionRequestDetails(
    positionRequestId,
    !!positionRequestId
  );
  const config = useEntityFormConfig(ApiCFEntityTypes.job);

  if (isJobLoading || isPositionRequestLoading || config.isPending) {
    return (
      <FullScreenPage isCenterVertical={true}>
        <Stack gap={"md"} className={"text-center"}>
          <Skeleton className={"h-full w-140"} />
        </Stack>
      </FullScreenPage>
    );
  }

  return <JobEditForm jobId={jobId} job={job} positionRequest={positionRequest} config={config} />;
};

interface JobEditFormProps {
  jobId: number;
  job: ApiJob | undefined;
  positionRequest: ApiHiringPlanRequest | undefined;
  config: ReturnType<typeof useEntityFormConfig>;
}

function JobEditForm({ jobId, job, positionRequest, config }: JobEditFormProps) {
  const { t } = useTranslation();
  const { step: stepParam } = useParams<{ step?: string }>();
  const { isFeatureEnabled } = usePermissions();
  const navigate = useNavigate();
  const [jobSaved, setJobSaved] = useState<ApiJob | undefined>(job);

  const initialNavigationData = useRef<{
    hasOpener: boolean;
    referrer: string;
    historyLength: number;
  } | null>(null);

  useEffect(() => {
    if (initialNavigationData.current === null) {
      let referrerPath = "";

      const savedReferrer = sessionStorage.getItem("job_edit_referrer");

      if (savedReferrer) {
        referrerPath = savedReferrer;
        sessionStorage.removeItem("job_edit_referrer");
      } else if (document.referrer) {
        try {
          const referrerUrl = new URL(document.referrer);
          referrerPath = referrerUrl.pathname + referrerUrl.search;
        } catch {
          referrerPath = "";
        }
      }

      initialNavigationData.current = {
        hasOpener: !!window.opener,
        referrer: referrerPath,
        historyLength: history.length,
      };
    }
  }, []);

  const currentStep = useMemo(() => {
    if (stepParam && Object.values(JobEditStep).includes(stepParam as JobEditStep)) {
      return stepParam as JobEditStep;
    }
    return JobEditStep.details;
  }, [stepParam]);

  const [step, setStep] = useState<JobEditStep>(currentStep);

  useEffect(() => {
    setStep(currentStep);
  }, [currentStep]);

  const isApprovalsEnabled = isFeatureEnabled(ApiFeatureType.approvals);
  const sections = useMemo(
    () => getJobFormSections(config.sections, isApprovalsEnabled),
    [config.sections, isApprovalsEnabled]
  );
  const formFields = useMemo(() => sections.flatMap((section) => section.fields), [sections]);
  const customFields = useMemo(
    () => formFields.filter((field) => field.type === ApiFieldTypes.custom),
    [formFields]
  );
  const { getZodSchemaForField, getValueForFieldFromValueObject, transformFormDataToValue } =
    useCustomFieldsHook();

  const employeeConfigSchema = z.array(
    z.object({ employee_id: z.number(), can_view_salary: z.boolean() })
  );

  const isAiShown =
    isFeatureEnabled(ApiFeatureType.ats_ai) &&
    formFields.some((field) => field.type === ApiFieldTypes.job_description);

  const buildJobSchema = (isMandatoryStatus: boolean) => {
    const requiredMessage = i18n.t("errors:field_required");
    const mandatoryNames = new Set(
      isMandatoryStatus
        ? formFields
            .filter((field) => field.is_mandatory)
            .flatMap((field) => jobFieldNames[field.type] ?? [])
        : []
    );
    const mandatory = <T extends z.ZodType>(name: keyof JobEditSystemFormData, schema: T) =>
      mandatoryNames.has(name)
        ? schema.refine((value) => !isJobFieldValueEmpty(value), requiredMessage)
        : schema;

    return z
      .object({
        title: z.string({ error: requiredMessage }).min(1, requiredMessage),
        status: z.enum(JobStatus),
        priority: mandatory("priority", z.enum(JobPriority).nullable()),
        division_id: mandatory("division_id", z.number().nullable()),
        department_id: mandatory("department_id", z.number().nullable()),
        job_profile_id: mandatory("job_profile_id", z.number().nullable()),
        location_id: mandatory("location_id", z.number().nullable()),
        salary_min: mandatory("salary_min", z.number().nullable()),
        salary_max: mandatory("salary_max", z.number().nullable()),
        currency_code: mandatory("currency_code", z.string().nullable()),
        pipeline_id: z.number({ error: requiredMessage }),
        description: mandatory("description", z.string().nullable()),
        emoji: z.string({ error: requiredMessage }).min(1, requiredMessage),
        approval_policy_id: mandatory("approval_policy_id", z.number().nullable()),
        publish_on_career_site: z.boolean(),
        career_site_ids: z.array(z.number()).nullable(),
        job_archiving_reason_id: z.number().nullable(),
        use_ai: z.boolean(),
        ai_requirements: z.string().nullable(),
        requirements: mandatory("requirements", z.string().nullable()),
        responsibilities: mandatory("responsibilities", z.string().nullable()),
        conditions: mandatory("conditions", z.string().nullable()),
        public_title: mandatory("public_title", z.string().nullable()),
        career_site_apply_config: z
          .array(
            z.object({
              section_id: z.number(),
              fields: z.array(
                z.object({
                  field_id: z.number(),
                  is_mandatory: z.boolean(),
                  is_hidden: z.boolean(),
                })
              ),
            })
          )
          .nullable(),
        recruiters_config: mandatory("recruiters_config", employeeConfigSchema),
        stakeholders_config: mandatory("stakeholders_config", employeeConfigSchema),
        files: mandatory("files", z.array(z.custom<ApiFile>())),
      })
      .and(
        z.object(
          Object.assign(
            {},
            ...customFields.map((field) =>
              getZodSchemaForField({
                ...field,
                is_mandatory: field.is_mandatory && isMandatoryStatus,
              })
            )
          )
        )
      )
      .superRefine((data, ctx) => {
        if (
          isAiShown &&
          data.use_ai &&
          (!data.ai_requirements || data.ai_requirements.trim() === "")
        ) {
          ctx.addIssue({
            code: "custom",
            message: requiredMessage,
            path: ["ai_requirements"],
          });
        }
      });
  };

  const getInitialValues = (): JobEditFormData => {
    const plan = positionRequest?.hiring_plan_data;
    const activeId = <T extends { id: number; is_archived: boolean }>(item?: T | null) =>
      item && !item.is_archived ? item.id : null;
    const salary = (value?: string | number | null) =>
      value ? toNullableSalaryNumber(value) : null;
    const source = job ?? plan;
    const customValues = Object.assign(
      {},
      ...customFields.map((field) =>
        getValueForFieldFromValueObject(
          field,
          job?.custom_fields?.find((item) => item.field.id === field.id)?.value,
          !job
        )
      )
    );
    return {
      title: source?.title ?? "",
      status: job?.status ?? JobStatus.published,
      priority: job?.priority ?? null,
      description: source?.description ?? "",
      requirements: source?.requirements ?? "",
      responsibilities: source?.responsibilities ?? "",
      conditions: job?.conditions ?? (plan ? null : ""),
      division_id: activeId(source?.division),
      department_id: activeId(source?.department),
      salary_min: salary(source?.salary_min),
      salary_max: salary(source?.salary_max),
      currency_code: source?.currency?.code ?? null,
      job_profile_id: activeId(source?.job_profile),
      location_id: activeId(source?.location),
      pipeline_id: job?.pipeline?.id ?? 0,
      emoji: job?.emoji ? job.emoji : getRandomDefaultEmoji(emojiDefaults.jobs),
      approval_policy_id: job?.approval_policy?.id ?? null,
      career_site_ids: job?.career_sites?.map((site) => site.id) ?? [],
      publish_on_career_site: (job?.career_sites.length ?? 0) > 0,
      job_archiving_reason_id: activeId(job?.job_archiving_reason),
      use_ai: job?.use_ai ?? false,
      ai_requirements: job?.ai_requirements ?? null,
      public_title: job?.public_title ?? null,
      career_site_apply_config: toJobApplyFormSave(job?.career_site_apply_config),
      recruiters_config: job?.recruiters_config ?? [],
      stakeholders_config: job?.stakeholders_config ?? [],
      files: job?.files ?? [],
      ...customValues,
    };
  };

  const jobResolver: Resolver<JobEditFormData> = (values, context, options) =>
    zodResolver(buildJobSchema(isJobMandatoryStatus(values.status)))(values, context, options);

  const formMethods = useForm<JobEditFormData>({
    mode: "onSubmit",
    resolver: jobResolver,
    defaultValues: getInitialValues(),
  });

  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    handleSubmit,
    clearErrors,
    getFieldState,
    formState: { errors, dirtyFields },
  } = formMethods;

  const watchEmoji = watch("emoji");
  const watchTitle = watch("title");
  const emojiColors = useMemo(() => getEmojiColorPalette(watchEmoji), [watchEmoji]);

  const steps = [JobEditStep.details, JobEditStep.career];
  const totalStepsLength = isFeatureEnabled(ApiFeatureType.ats_career_site)
    ? steps.length
    : steps.length - 1;

  const { data: pipelines } = usePipelines();

  useEffect(() => {
    if (pipelines && job?.pipeline == undefined && pipelines.length > 0) {
      const defaultPipeline = pipelines.find((x) => x.is_default) || pipelines[0];
      setValue("pipeline_id", defaultPipeline.id);
    }
  }, [pipelines]);

  const serverFieldMap = useMemo(
    () =>
      Object.fromEntries(
        formFields.flatMap((field) => {
          if (field.type === ApiFieldTypes.custom)
            return [[`custom_fields.${field.slug}`, field.slug]];
          const name = jobFieldNames[field.type];
          return name ? [[field.slug, name]] : [];
        })
      ),
    [formFields]
  );

  const jobSave = useJobSave(control, serverFieldMap);
  const { addRecent } = useRecent();

  const { handleErrors } = useErrorHandle();
  async function removeFile(file: ApiFile) {
    setValue(
      "files",
      getValues("files").filter((f) => f.id !== file.id),
      { shouldDirty: true }
    );
    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  const [recruiters, setRecruiters] = useState<ApiEmployee[]>(job?.recruiters || []);
  const [stakeholders, setStakeholders] = useState<ApiEmployee[]>(job?.stakeholders || []);

  const getPayload = (): ApiJobSavePayload => {
    const values = getValues();
    const payload: ApiJobSavePayload = {
      title: values.title,
      status: values.status,
      pipeline_id: values.pipeline_id,
      emoji: values.emoji,
      career_site_ids: values.publish_on_career_site ? (values.career_site_ids ?? []) : [],
      job_archiving_reason_id:
        values.status === JobStatus.archived ? values.job_archiving_reason_id : null,
      use_ai: values.use_ai,
      ai_requirements: values.ai_requirements,
      career_site_apply_config: getFieldState("career_site_apply_config").isDirty
        ? values.career_site_apply_config
        : undefined,
    };
    for (const field of formFields) {
      const name = jobFieldNames[field.type];
      if (name === "files") payload.file_uuids = values.files.map((file) => file.uuid);
      else if (name) Object.assign(payload, { [name]: values[name] });
    }
    if (customFields.length > 0) {
      payload.custom_fields = Object.fromEntries(
        customFields.map((field) => [field.slug, transformFormDataToValue(field, values)])
      );
    }
    return payload;
  };

  const saveJob = async (showFinalStep: boolean = true) => {
    const payload: ApiJobSavePayload = getPayload();

    clearErrors("root");

    try {
      const jobSaved = await jobSave.mutateAsync({ id: jobId, payload });
      if (jobId > 0) {
        analyticsService.trackEvent(analyticEvents.jobs.edited, {
          [analyticProperties.id]: jobSaved.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.jobs.created, {
          [analyticProperties.id]: jobSaved.id,
        });
      }

      setJobSaved(jobSaved);

      if (dirtyFields.currency_code && payload.currency_code) {
        addRecent([{ type: ApiRecentKey.currency, values: [payload.currency_code] }]);
      }

      if (showFinalStep) {
        setStep(JobEditStep.final);
      }

      return jobSaved;
    } catch {
      if (
        step !== JobEditStep.details &&
        stepFields[JobEditStep.details]?.some((name) => getFieldState(name).error)
      ) {
        navigate(`/recruitment/jobs/${jobId}/edit/${JobEditStep.details}`);
      }
      return undefined;
    }
  };

  const onSubmit = handleSubmit(
    async () => {
      await saveJob(true);
    },
    () => {
      if (step !== JobEditStep.details) {
        navigate(`/recruitment/jobs/${jobId}/edit/${JobEditStep.details}`);
      }
    }
  );

  const closePage = () => {
    const navData = initialNavigationData.current;

    if (!navData) {
      navigate("/recruitment/jobs");

      return;
    }

    if (navData.referrer && !/\/recruitment\/jobs\/\d+\/edit\/career/.test(navData.referrer)) {
      navigate(navData.referrer);
    } else {
      navigate("/recruitment/jobs");
    }
  };

  const stepFields: Partial<Record<JobEditStep, Path<JobEditFormData>[]>> = {
    [JobEditStep.details]: [
      "status",
      "job_archiving_reason_id",
      "emoji",
      "use_ai",
      "ai_requirements",
      ...formFields.flatMap((field) => {
        if (field.type !== ApiFieldTypes.custom) return jobFieldNames[field.type] ?? [];
        if (field.field_type === ApiFieldValueTypes.url) return [field.slug, `${field.slug}_label`];
        return field.field_type === ApiFieldValueTypes.currency
          ? [field.slug, `${field.slug}_currency`]
          : [field.slug];
      }),
    ],
    [JobEditStep.career]: ["publish_on_career_site", "career_site_ids"],
  };

  const handleNext = async () => {
    if (step === JobEditStep.final || !config.isSuccess) return;

    const isValid = await trigger(stepFields[step]);

    if (!isValid) return;

    if (step === JobEditStep.details) {
      if (!isFeatureEnabled(ApiFeatureType.ats_career_site)) {
        await onSubmit();
        return;
      }
      if (jobId === 0) {
        const savedJob = await saveJob(false);
        if (savedJob) {
          navigate(`/recruitment/jobs/${savedJob.id}/edit/${JobEditStep.career}`, {
            replace: true,
          });
        }
        return;
      }
      navigate(`/recruitment/jobs/${jobId}/edit/${JobEditStep.career}`);
    } else if (step === JobEditStep.career) {
      await onSubmit();
    }
  };

  const getLastStep = () => {
    return isFeatureEnabled(ApiFeatureType.ats_career_site)
      ? JobEditStep.career
      : JobEditStep.details;
  };
  const isLastStep = () => step === getLastStep();
  const isSaveStep = () => {
    if (!job && step === JobEditStep.details) return true;

    return isLastStep();
  };

  const handleBack = () => {
    if (step === JobEditStep.career) {
      navigate(`/recruitment/jobs/${jobId}/edit/${JobEditStep.details}`);
    }
  };

  if (step == JobEditStep.final) {
    return <JobEditFinal jobSaved={jobSaved} onClose={closePage} />;
  }

  const dataContextValue: JobEditDataContextType = {
    job,
    jobId,
    sections,
    getPayload,
    removeFile,
    recruiters,
    setRecruiters,
    stakeholders,
    setStakeholders,
  };

  return (
    <>
      <FullScreenPage
        isCenterVertical={false}
        backSlot={
          <Button variant={"secondary"} size={"lg"} onClick={closePage}>
            {t("common:exit")}
          </Button>
        }
        actionsSlot={
          <Stack direction="horizontal" gap="sm">
            {step !== JobEditStep.details && (
              <Button variant="secondary" size="lg" onClick={handleBack}>
                {t("common:back")}
              </Button>
            )}
            <Button size="lg" onClick={handleNext} isLoading={isSaveStep() && jobSave.isPending}>
              {isSaveStep() ? t("common:save") : t("common:next")}
            </Button>
          </Stack>
        }
        backgroundSlot={
          emojiColors ? (
            <div className="absolute inset-0 [[data-color-scheme=dark]_&]:opacity-10">
              <GradientContainer emoji={watchEmoji} addLightOverlayToBottom={true} />
            </div>
          ) : undefined
        }
        titleSlot={
          <DotSeparator className={"inline-flex"}>
            <Text className={"font-semibold"}>{t("ats:job")}</Text>
            {steps.indexOf(step) > 0 && (
              <Stack gap={"sm"} direction={"horizontal"} items={"center"}>
                <Avatar emoji={watchEmoji} size={"sm"} />
                <Text className={"font-semibold"}>{watchTitle}</Text>
              </Stack>
            )}
            <Text>
              {t("core:step_of", {
                currentStep: steps.indexOf(step) + 1,
                countSteps: totalStepsLength,
              })}
            </Text>
          </DotSeparator>
        }
        sidebarSlot={<JobEditSidebar step={step} job={job} />}
      >
        <JobEditFormContext.Provider value={formMethods}>
          <JobEditDataContext.Provider value={dataContextValue}>
            <form onSubmit={onSubmit} className={"gap-sm flex flex-col relative"}>
              <Stack gap="sm" className={step === JobEditStep.details ? undefined : "hidden"}>
                <JobEditDetails />
              </Stack>
              {step === JobEditStep.career && <JobEditCareer />}

              <ServerErrorField errors={errors} />
            </form>
          </JobEditDataContext.Provider>
        </JobEditFormContext.Provider>
      </FullScreenPage>
    </>
  );
}
